import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LegalAcceptances from '@/components/LegalAcceptances';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, Megaphone, PoundSterling, Edit, AlertCircle, Settings, Trash2,
  Building2, Users, UserPlus, Tag, Phone, Mail, Globe, MapPin,
  MessageSquare, Send, CheckCircle, XCircle, Key
} from 'lucide-react';
import { toast } from 'sonner';
import AnnouncementsManager from '@/components/AnnouncementsManager';
import TagsManager from '@/components/TagsManager';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user, setUser, selectedBusiness, setSelectedBusiness, setSelectedCampaign } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [campaigns, setCampaigns] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Campaign Modal
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    lead_value: 450,
    deposit_amount: 100
  });

  // Business Modal
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessForm, setBusinessForm] = useState({
    name: '',
    description: '',
    primary_color: '#00B4D8',
    phone: '',
    email: '',
    website: '',
    address: ''
  });
  const [businessStep, setBusinessStep] = useState(1); // 1 = Info, 2 = Integrations
  const [integrationForm, setIntegrationForm] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    sendgrid_api_key: '',
    sendgrid_sender_email: ''
  });

  // User Modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    business_id: ''
  });

  const dispatchDataRefresh = useCallback((scope = 'campaigns') => {
    window.dispatchEvent(new CustomEvent('beluga:data-refresh', { detail: { scope } }));
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch campaigns
      let campaignsUrl = `${API_URL}/api/campaigns`;
      if (user?.role === 'super_admin' && selectedBusiness) {
        campaignsUrl += `?business_id=${selectedBusiness.business_id}`;
      }
      const campaignsRes = await fetch(campaignsUrl, { credentials: 'include', headers });
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());

      // Fetch businesses (super_admin only)
      if (user?.role === 'super_admin') {
        const bizRes = await fetch(`${API_URL}/api/businesses`, { credentials: 'include', headers });
        if (bizRes.ok) setBusinesses(await bizRes.json());
      }

      // Fetch users
      let usersUrl = `${API_URL}/api/users`;
      if (user?.role === 'super_admin' && selectedBusiness) {
        usersUrl += `?business_id=${selectedBusiness.business_id}`;
      }
      const usersRes = await fetch(usersUrl, { credentials: 'include', headers });
      if (usersRes.ok) setUsers(await usersRes.json());

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Campaign handlers
  const handleCreateCampaign = async () => {
    try {
      if (!editingCampaign && user?.role === 'super_admin' && !selectedBusiness) {
        toast.error('Select a business before creating a campaign');
        return;
      }

      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      let url = editingCampaign 
        ? `${API_URL}/api/campaigns/${editingCampaign.campaign_id}`
        : `${API_URL}/api/campaigns`;
      
      if (!editingCampaign && user?.role === 'super_admin' && selectedBusiness) {
        url += `?business_id=${selectedBusiness.business_id}`;
      }

      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(campaignForm)
      });

      if (response.ok) {
        const savedCampaign = await response.json();
        toast.success(editingCampaign ? 'Campaign updated' : 'Campaign created');
        setCampaigns((prev) => (
          editingCampaign
            ? prev.map((existing) => existing.campaign_id === savedCampaign.campaign_id ? savedCampaign : existing)
            : [savedCampaign, ...prev]
        ));
        setSelectedCampaign(savedCampaign);
        dispatchDataRefresh('campaigns');
        setShowCampaignModal(false);
        setEditingCampaign(null);
        setCampaignForm({ name: '', description: '', lead_value: 450, deposit_amount: 100 });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Failed to save campaign');
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      description: campaign.description || '',
      lead_value: campaign.lead_value,
      deposit_amount: campaign.deposit_amount
    });
    setShowCampaignModal(true);
  };

  // Business handlers
  const handleCreateBusiness = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const url = editingBusiness 
        ? `${API_URL}/api/businesses/${editingBusiness.business_id}`
        : `${API_URL}/api/businesses`;

      const response = await fetch(url, {
        method: editingBusiness ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(businessForm)
      });

      if (response.ok) {
        const savedBusiness = await response.json();

        if (!editingBusiness && user?.role === 'super_admin') {
          setBusinesses((prev) => [savedBusiness, ...prev]);
          setSelectedBusiness(savedBusiness);
          setUser((prev) => prev ? { ...prev, current_business_id: savedBusiness.business_id } : prev);
        }
        
        // If creating new business and has integrations, save them
        if (!editingBusiness && (integrationForm.twilio_account_sid || integrationForm.sendgrid_api_key)) {
          await saveBusinessIntegrations(savedBusiness.business_id);
        }
        
        toast.success(editingBusiness ? 'Business updated' : 'Business created');
        dispatchDataRefresh('businesses');
        fetchData();
        closeBusinessModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save business');
      }
    } catch (error) {
      console.error('Error saving business:', error);
      toast.error('Failed to save business');
    }
  };

  const saveBusinessIntegrations = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/business/${businessId}/credentials`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(integrationForm)
      });
      
      if (!response.ok) {
        console.error('Failed to save integrations');
      }
    } catch (error) {
      console.error('Error saving integrations:', error);
    }
  };

  const handleDeleteBusiness = async (business) => {
    if (!confirm(`Delete ${business.name}? This will archive its campaigns and forms.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/businesses/${business.business_id}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to delete business');
        return;
      }

      const result = await response.json();
      const nextBusiness = businesses.find((entry) => entry.business_id === result.next_business_id) || null;

      setBusinesses((prev) => prev.filter((entry) => entry.business_id !== business.business_id));
      if (selectedBusiness?.business_id === business.business_id) {
        setSelectedBusiness(nextBusiness);
        setSelectedCampaign(null);
      }
      if (user?.role === 'super_admin') {
        setUser((prev) => prev ? { ...prev, current_business_id: result.next_business_id || null } : prev);
      }

      toast.success('Business deleted');
      dispatchDataRefresh('businesses');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const loadBusinessIntegrations = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/business/${businessId}/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIntegrationForm({
          twilio_account_sid: '',
          twilio_auth_token: '',
          twilio_phone_number: data.twilio_phone_number || '',
          sendgrid_api_key: '',
          sendgrid_sender_email: data.sendgrid_sender_email || ''
        });
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  const openBusinessModal = (business = null) => {
    if (business) {
      setEditingBusiness(business);
      setBusinessForm({
        name: business.name,
        description: business.description || '',
        primary_color: business.primary_color,
        phone: business.phone || '',
        email: business.email || '',
        website: business.website || '',
        address: business.address || ''
      });
      loadBusinessIntegrations(business.business_id);
    } else {
      setEditingBusiness(null);
      setBusinessForm({
        name: '',
        description: '',
        primary_color: '#00B4D8',
        phone: '',
        email: '',
        website: '',
        address: ''
      });
      setIntegrationForm({
        twilio_account_sid: '',
        twilio_auth_token: '',
        twilio_phone_number: '',
        sendgrid_api_key: '',
        sendgrid_sender_email: ''
      });
    }
    setBusinessStep(1);
    setShowBusinessModal(true);
  };

  const closeBusinessModal = () => {
    setShowBusinessModal(false);
    setEditingBusiness(null);
    setBusinessStep(1);
    setBusinessForm({
      name: '',
      description: '',
      primary_color: '#00B4D8',
      phone: '',
      email: '',
      website: '',
      address: ''
    });
    setIntegrationForm({
      twilio_account_sid: '',
      twilio_auth_token: '',
      twilio_phone_number: '',
      sendgrid_api_key: '',
      sendgrid_sender_email: ''
    });
  };

  // User handlers
  const manageUser = async (u, action) => {
    let url = `${API_URL}/api/users/${u.user_id}`;
    let method = 'POST';
    let body;
    if (action === 'delete') {
      if (!window.confirm(`Delete ${u.name} (${u.email})? They lose access immediately. Their centre's data is kept.`)) return;
      method = 'DELETE';
    } else if (action === 'reset') {
      const pw = window.prompt(`New password for ${u.name} (at least 10 characters). They'll be signed out everywhere.`);
      if (!pw) return;
      url += '/reset-password';
      body = { new_password: pw };
    } else {
      if (action === 'disable' && !window.confirm(`Disable ${u.name}? They're signed out and can't log in until re-enabled.`)) return;
      url += '/disable';
      body = { disabled: action === 'disable' };
    }
    const res = await fetch(url, {
      method, credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      toast.success(action === 'delete' ? 'User deleted' : action === 'reset' ? 'Password reset' : action === 'disable' ? 'User disabled' : 'User enabled');
      fetchData();
    } else {
      toast.error(data.detail || 'Action failed');
    }
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          ...userForm,
          business_id: userForm.business_id || (user?.role === 'admin' ? user.business_id : selectedBusiness?.business_id)
        })
      });

      if (response.ok) {
        toast.success('User created successfully');
        fetchData();
        setShowUserModal(false);
        setUserForm({ name: '', email: '', password: '', role: 'user', business_id: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    }
  };

  // Check if user has admin access
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Admin Access Required</h2>
          <p className="text-slate-500 mt-2">You don't have permission to access this page</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Admin Panel
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.role === 'super_admin' ? 'Manage businesses, campaigns, and users' : 'Manage campaigns and users'}
          </p>
        </div>
      </div>

      <Tabs defaultValue={user?.role === 'super_admin' ? 'businesses' : 'campaigns'}>
        <TabsList>
          {user?.role === 'super_admin' && (
            <TabsTrigger value="businesses">Businesses</TabsTrigger>
          )}
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="announcements">Announcements</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          {user?.role === 'super_admin' && (
            <TabsTrigger value="legal">Legal</TabsTrigger>
          )}
        </TabsList>

        {user?.role === 'super_admin' && (
          <TabsContent value="legal" className="space-y-4">
            <LegalAcceptances />
          </TabsContent>
        )}

        {/* Businesses Tab (Super Admin Only) */}
        {user?.role === 'super_admin' && (
          <TabsContent value="businesses" className="space-y-4">
            <div className="flex justify-end">
              <Button 
                onClick={() => openBusinessModal()}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                data-testid="create-business-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Business
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-cyan-500" />
                  Businesses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {businesses.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No businesses yet. Create your first one!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {businesses.map((business) => (
                      <div 
                        key={business.business_id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors gap-4"
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                            style={{ backgroundColor: business.primary_color }}
                          >
                            {business.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900">{business.name}</h3>
                            {business.description && (
                              <p className="text-sm text-slate-500 truncate">{business.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {business.phone && (
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {business.phone}
                                </span>
                              )}
                              {business.email && (
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {business.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {business.has_twilio ? (
                            <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> SMS
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 flex items-center gap-1">
                              <XCircle className="w-3 h-3" /> SMS
                            </Badge>
                          )}
                          {business.has_sendgrid ? (
                            <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Email
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 flex items-center gap-1">
                              <XCircle className="w-3 h-3" /> Email
                            </Badge>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openBusinessModal(business)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBusiness(business)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { setEditingCampaign(null); setCampaignForm({ name: '', description: '', lead_value: 450, deposit_amount: 100 }); setShowCampaignModal(true); }}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-cyan-500" />
                Campaigns
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No campaigns yet. Create your first one!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map((campaign) => (
                    <div 
                      key={campaign.campaign_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                          {campaign.is_active && (
                            <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                          )}
                        </div>
                        {campaign.description && (
                          <p className="text-sm text-slate-500 mt-1">{campaign.description}</p>
                        )}
                        {isSuperAdmin && (
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <PoundSterling className="w-3 h-3" />
                              Lead: £{campaign.lead_value}
                            </span>
                            <span className="flex items-center gap-1">
                              <PoundSterling className="w-3 h-3" />
                              Deposit: £{campaign.deposit_amount}
                            </span>
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditCampaign(campaign)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              onClick={() => { setUserForm({ name: '', email: '', password: '', role: 'user', business_id: '' }); setShowUserModal(true); }}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
              data-testid="create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              New User
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-500" />
                Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No users found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {users.map((u) => (
                    <div 
                      key={u.user_id}
                      className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-bold">
                          {u.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{u.name}</h3>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={
                          u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                          u.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {u.role?.replace('_', ' ')}
                        </Badge>
                        {u.business_name && (
                          <Badge variant="secondary">{u.business_name}</Badge>
                        )}
                        {u.disabled && <Badge className="bg-red-100 text-red-700">Disabled</Badge>}
                        {u.role !== 'super_admin' && u.user_id !== user?.user_id && (
                          <>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => manageUser(u, u.disabled ? 'enable' : 'disable')}>
                              {u.disabled ? 'Enable' : 'Disable'}
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => manageUser(u, 'reset')}>
                              Reset password
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => manageUser(u, 'delete')}>
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-cyan-500" />
                Webhook Endpoints
              </CardTitle>
              <CardDescription>
                Use these URLs to connect your lead sources
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Facebook/Meta Webhook</p>
                <code className="text-xs bg-white px-3 py-2 rounded border block overflow-x-auto">
                  {API_URL}/api/webhook/facebook
                </code>
                <p className="text-xs text-slate-500 mt-2">
                  Verify Token: set <code className="bg-white px-1">FB_WEBHOOK_VERIFY_TOKEN</code> in the backend environment.
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">Website Form Webhook</p>
                <code className="text-xs bg-white px-3 py-2 rounded border block overflow-x-auto">
                  {API_URL}/api/webhook/website
                </code>
                <p className="text-xs text-slate-500 mt-2">
                  POST with: name, email, phone, source, campaign_id, business_id, and UTM params for tracking
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <AnnouncementsManager />
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags">
          <TagsManager />
        </TabsContent>
      </Tabs>

      {/* Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="campaign-name">Campaign Name</Label>
              <Input
                id="campaign-name"
                placeholder="e.g., Beauty Therapy, Aesthetics Course"
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="campaign-desc">Description (optional)</Label>
              <Input
                id="campaign-desc"
                placeholder="Brief description"
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                className="mt-1.5"
              />
            </div>
            {isSuperAdmin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lead-value">Lead Value (£)</Label>
                  <Input
                    id="lead-value"
                    type="number"
                    value={campaignForm.lead_value}
                    onChange={(e) => setCampaignForm({ ...campaignForm, lead_value: parseFloat(e.target.value) || 0 })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="deposit">Deposit Amount (£)</Label>
                  <Input
                    id="deposit"
                    type="number"
                    value={campaignForm.deposit_amount}
                    onChange={(e) => setCampaignForm({ ...campaignForm, deposit_amount: parseFloat(e.target.value) || 0 })}
                    className="mt-1.5"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCampaign}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
              disabled={!campaignForm.name.trim()}
            >
              {editingCampaign ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business Modal - Multi-step */}
      <Dialog open={showBusinessModal} onOpenChange={closeBusinessModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-cyan-500" />
              {editingBusiness ? 'Edit Business' : 'Create Business'}
            </DialogTitle>
            {/* Step indicator */}
            <div className="flex items-center gap-2 pt-2">
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${businessStep === 1 ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">1</span>
                Info
              </div>
              <div className="w-4 h-px bg-slate-300"></div>
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${businessStep === 2 ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">2</span>
                Integrations
              </div>
            </div>
          </DialogHeader>
          
          {/* Step 1: Business Info */}
          {businessStep === 1 && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="business-name">Business Name *</Label>
                  <Input
                    id="business-name"
                    placeholder="e.g., Sky Education"
                    value={businessForm.name}
                    onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="business-desc">Description</Label>
                  <Textarea
                    id="business-desc"
                    placeholder="Brief description of the business"
                    value={businessForm.description}
                    onChange={(e) => setBusinessForm({ ...businessForm, description: e.target.value })}
                    className="mt-1.5"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="business-phone">Phone</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="business-phone"
                      placeholder="+44 123 456 7890"
                      value={businessForm.phone}
                      onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="business-email">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="business-email"
                      type="email"
                      placeholder="contact@business.com"
                      value={businessForm.email}
                      onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="business-website">Website</Label>
                  <div className="relative mt-1.5">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="business-website"
                      placeholder="https://www.business.com"
                      value={businessForm.website}
                      onChange={(e) => setBusinessForm({ ...businessForm, website: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="business-color">Brand Color</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      id="business-color"
                      type="color"
                      value={businessForm.primary_color}
                      onChange={(e) => setBusinessForm({ ...businessForm, primary_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={businessForm.primary_color}
                      onChange={(e) => setBusinessForm({ ...businessForm, primary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="business-address">Address</Label>
                  <div className="relative mt-1.5">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      id="business-address"
                      placeholder="123 Business St, City, Country"
                      value={businessForm.address}
                      onChange={(e) => setBusinessForm({ ...businessForm, address: e.target.value })}
                      className="pl-10"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Integrations */}
          {businessStep === 2 && (
            <div className="space-y-6 py-4">
              {/* Twilio SMS */}
              <div className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-5 h-5 text-purple-500" />
                  <h4 className="font-medium">Twilio SMS</h4>
                  <Badge className="ml-auto bg-purple-100 text-purple-700 text-xs">SMS Notifications</Badge>
                </div>
                <p className="text-sm text-slate-500 mb-4">Connect Twilio to send SMS notifications to your leads.</p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="twilio-sid" className="text-sm">Account SID</Label>
                    <Input
                      id="twilio-sid"
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      value={integrationForm.twilio_account_sid}
                      onChange={(e) => setIntegrationForm({ ...integrationForm, twilio_account_sid: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twilio-token" className="text-sm">Auth Token</Label>
                    <Input
                      id="twilio-token"
                      type="password"
                      placeholder="Your Twilio auth token"
                      value={integrationForm.twilio_auth_token}
                      onChange={(e) => setIntegrationForm({ ...integrationForm, twilio_auth_token: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twilio-phone" className="text-sm">Twilio Phone Number</Label>
                    <Input
                      id="twilio-phone"
                      placeholder="+1234567890"
                      value={integrationForm.twilio_phone_number}
                      onChange={(e) => setIntegrationForm({ ...integrationForm, twilio_phone_number: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* SendGrid Email */}
              <div className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Send className="w-5 h-5 text-blue-500" />
                  <h4 className="font-medium">SendGrid Email</h4>
                  <Badge className="ml-auto bg-blue-100 text-blue-700 text-xs">Email Notifications</Badge>
                </div>
                <p className="text-sm text-slate-500 mb-4">Connect SendGrid to send email notifications to your leads.</p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="sendgrid-key" className="text-sm">API Key</Label>
                    <Input
                      id="sendgrid-key"
                      type="password"
                      placeholder="SG.xxxxxxxxxxxxxxxxxxxx"
                      value={integrationForm.sendgrid_api_key}
                      onChange={(e) => setIntegrationForm({ ...integrationForm, sendgrid_api_key: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sendgrid-email" className="text-sm">Sender Email</Label>
                    <Input
                      id="sendgrid-email"
                      type="email"
                      placeholder="noreply@yourbusiness.com"
                      value={integrationForm.sendgrid_sender_email}
                      onChange={(e) => setIntegrationForm({ ...integrationForm, sendgrid_sender_email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-400 text-center">
                You can skip integrations now and add them later in Settings.
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {businessStep === 1 ? (
              <>
                <Button variant="outline" onClick={closeBusinessModal}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => setBusinessStep(2)}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                  disabled={!businessForm.name.trim()}
                >
                  Next: Integrations
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setBusinessStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={handleCreateBusiness}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                >
                  {editingBusiness ? 'Update Business' : 'Create Business'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="user-name">Full Name</Label>
              <Input
                id="user-name"
                placeholder="John Smith"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                placeholder="john@example.com"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="user-password">Password</Label>
              <Input
                id="user-password"
                type="password"
                placeholder="••••••••"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="user-role">Role</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(v) => setUserForm({ ...userForm, role: v })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  {user?.role === 'super_admin' && (
                    <SelectItem value="admin">Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {user?.role === 'super_admin' && (
              <div>
                <Label htmlFor="user-business">Business</Label>
                <Select 
                  value={userForm.business_id || selectedBusiness?.business_id} 
                  onValueChange={(v) => setUserForm({ ...userForm, business_id: v })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((b) => (
                      <SelectItem key={b.business_id} value={b.business_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
              disabled={!userForm.name.trim() || !userForm.email.trim() || !userForm.password.trim()}
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
